import './QuizQuestion.css';
import { Fragment, useState, useRef, useEffect, useMemo } from 'react';

function LinkingQuestion({ question, pairs, onPairsChange }) {
    // Ensure pairs is an array
    const currentPairs = useMemo(() => Array.isArray(pairs) ? pairs : [], [pairs]);

    // Track currently selected left item
    const [selectedLeft, setSelectedLeft] = useState(null);

    // Refs for measuring positions
    const leftRefs = useRef({});
    const rightRefs = useRef({});
    const containerRef = useRef(null);
    const [, forceUpdate] = useState({});

    // Force re-render when pairs change to update SVG lines
    useEffect(() => {
        forceUpdate({});
    }, [currentPairs]);

    // Color palette for pairs
    const pairColors = [
        '#4CAF50', // Green
        '#2196F3', // Blue
        '#FF9800', // Orange
        '#9C27B0', // Purple
        '#00BCD4', // Cyan
        '#FFEB3B', // Yellow
        '#E91E63', // Pink
        '#FF5722'  // Deep Orange
    ];

    const getPairColor = (leftId) => {
        const pairIndex = currentPairs.findIndex(p => p.left === leftId);
        if (pairIndex === -1) return null;
        return pairColors[pairIndex % pairColors.length];
    };

    const handleLeftClick = (leftId) => {
        setSelectedLeft(leftId);
    };

    const handleRightClick = (rightId) => {
        if (selectedLeft) {
            // Check if this right item is already linked to a different left item
            const existingRightPair = currentPairs.find(p => p.right === rightId);
            if (existingRightPair && existingRightPair.left !== selectedLeft) {
                // Right item is already linked to another left item, do nothing
                setSelectedLeft(null);
                return;
            }

            // Check if this left item already has a pair
            const existingPairIndex = currentPairs.findIndex(p => p.left === selectedLeft);

            let newPairs;
            if (existingPairIndex !== -1) {
                // Update existing pair
                newPairs = [...currentPairs];
                newPairs[existingPairIndex] = { left: selectedLeft, right: rightId };
            } else {
                // Add new pair
                newPairs = [...currentPairs, { left: selectedLeft, right: rightId }];
            }

            onPairsChange(newPairs);
            setSelectedLeft(null);
        }
    };

    const handlePairRemove = (leftId) => {
        const newPairs = currentPairs.filter(p => p.left !== leftId);
        onPairsChange(newPairs);
    };

    const getLinkedRight = (leftId) => {
        const pair = currentPairs.find(p => p.left === leftId);
        return pair ? pair.right : null;
    };

    const isRightLinked = (rightId) => {
        return currentPairs.some(p => p.right === rightId);
    };

    const getRightItemColor = (rightId) => {
        const pair = currentPairs.find(p => p.right === rightId);
        if (!pair) return null;
        return getPairColor(pair.left);
    };

    const getCurvedPath = (pair) => {
        const leftEl = leftRefs.current[pair.left];
        const rightEl = rightRefs.current[pair.right];
        const container = containerRef.current;

        if (!leftEl || !rightEl || !container) return null;

        const containerRect = container.getBoundingClientRect();
        const leftRect = leftEl.getBoundingClientRect();
        const rightRect = rightEl.getBoundingClientRect();

        // Calculate positions relative to container
        const x1 = leftRect.right - containerRect.left;
        const y1 = leftRect.top + leftRect.height / 2 - containerRect.top;
        const x2 = rightRect.left - containerRect.left;
        const y2 = rightRect.top + rightRect.height / 2 - containerRect.top;

        // Control points for bezier curve
        const controlPointOffset = (x2 - x1) / 2;
        const cx1 = x1 + controlPointOffset;
        const cy1 = y1;
        const cx2 = x2 - controlPointOffset;
        const cy2 = y2;

        return `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
    };

    return (
        <div className="linking-question">
            <h2 className="question-text">{question.questionText}</h2>

            <div className="linking-instruction">
                Klikněte na položku vlevo, poté na odpovídající položku vpravo.
            </div>

            <div className="linking-container" ref={containerRef}>
                <svg className="linking-svg">
                    {currentPairs.map((pair, index) => {
                        const path = getCurvedPath(pair);
                        if (!path) return null;
                        const color = getPairColor(pair.left);
                        return (
                            <path
                                key={index}
                                d={path}
                                className="link-curve"
                                stroke={color}
                                strokeWidth="3"
                                fill="none"
                                style={{ filter: `drop-shadow(0 0 3px ${color}80)` }}
                            />
                        );
                    })}
                </svg>

                {question.leftItems.map((leftItem, i) => {
                    const rightItem = question.rightItems[i];
                    const linkedRight = getLinkedRight(leftItem.id);
                    const isSelected = selectedLeft === leftItem.id;
                    const isLinked = linkedRight !== null;
                    const pairColor = getPairColor(leftItem.id);
                    const rightIsLinked = rightItem ? isRightLinked(rightItem.id) : false;
                    const rightPairColor = rightItem ? getRightItemColor(rightItem.id) : null;

                    return (
                        <Fragment key={leftItem.id}>
                            <div
                                ref={el => leftRefs.current[leftItem.id] = el}
                                className={`linking-item ${isSelected ? 'selected' : ''} ${isLinked ? 'linked' : ''}`}
                                onClick={() => handleLeftClick(leftItem.id)}
                                style={isLinked ? {
                                    borderColor: pairColor,
                                    backgroundColor: `${pairColor}20`
                                } : {}}
                            >
                                <div className="linking-item-text">{leftItem.text}</div>
                                {isLinked && (
                                    <button
                                        className="remove-link-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePairRemove(leftItem.id);
                                        }}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {rightItem && (
                                <div
                                    ref={el => rightRefs.current[rightItem.id] = el}
                                    className={`linking-item ${rightIsLinked ? 'linked' : ''} ${selectedLeft ? 'clickable' : ''}`}
                                    onClick={() => handleRightClick(rightItem.id)}
                                    style={rightIsLinked ? {
                                        borderColor: rightPairColor,
                                        backgroundColor: `${rightPairColor}20`
                                    } : {}}
                                >
                                    <div className="linking-item-text">{rightItem.text}</div>
                                </div>
                            )}
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
}

export default LinkingQuestion;
